import type {
  DbpjkitWorkerJobRunsBoolExp,
  DbpjkitWorkerJobsBoolExp,
  DbpjkitWorkerJobsOrderBy,
  OrderBy,
} from "~/common/types/graphql";
import type { BadgeProps } from "~/components/ui";
import type {
  GetDbpjKitWorkerJobRunsQueryVariables,
  GetDbpjKitWorkerJobsQueryVariables,
} from "~/features/dbpjkitWorkerJobs/gqls";
import type {
  JobListFilters,
  JobRaw,
  JobSortOrder,
  JobSummary,
  SortDirection,
} from "~/features/dbpjkitWorkerJobs/types";

const orderValue = (direction: SortDirection): OrderBy => (direction === "asc" ? "ASC" : "DESC");

/**
 * Build the jobs-list `where` from the two text filters (V1 parity): `commandName`
 * and `googleSpreadsheetId` are matched case-insensitively (`_ilike`). Returns
 * `undefined` when no filter is active, a bare condition for one, `_and` for both.
 */
function buildJobsWhere(filters: JobListFilters): GetDbpjKitWorkerJobsQueryVariables["where"] {
  const conditions: DbpjkitWorkerJobsBoolExp[] = [];
  if (filters.commandName.trim()) {
    conditions.push({
      commandName: { _ilike: `%${filters.commandName.trim()}%` },
    });
  }
  if (filters.spreadsheetId.trim()) {
    conditions.push({
      googleSpreadsheetId: { _ilike: `%${filters.spreadsheetId.trim()}%` },
    });
  }
  if (conditions.length === 0) return undefined;
  if (conditions.length === 1) return conditions[0];
  return { _and: conditions };
}

/** Assemble the full `GetDBPJKitWorkerJobs` variables for a page of the list. */
export function buildJobsVariables(args: {
  tenantId: string;
  filters: JobListFilters;
  sortOrder: JobSortOrder;
  direction: SortDirection;
  pageIndex: number;
  pageSize: number;
}): GetDbpjKitWorkerJobsQueryVariables {
  const { tenantId, filters, sortOrder, direction, pageIndex, pageSize } = args;
  return {
    tenantId,
    limit: pageSize,
    offset: pageIndex * pageSize,
    where: buildJobsWhere(filters),
    order_by: {
      [sortOrder]: orderValue(direction),
    } satisfies DbpjkitWorkerJobsOrderBy,
  };
}

/** Variables for a single job lookup (detail page / peek), mirroring V1's `id _eq` fetch. */
export function buildJobDetailVariables(tenantId: string, id: string): GetDbpjKitWorkerJobsQueryVariables {
  return {
    tenantId,
    limit: 1,
    offset: 0,
    where: { id: { _eq: id } },
    order_by: { createdAt: "DESC" } satisfies DbpjkitWorkerJobsOrderBy,
  };
}

/** Variables for a page of one job's run history, optionally narrowed by status (V1 parity). */
export function buildRunsVariables(args: {
  tenantId: string;
  jobId: string;
  statusFilter: string | "all";
  pageIndex: number;
  pageSize: number;
}): GetDbpjKitWorkerJobRunsQueryVariables {
  const { tenantId, jobId, statusFilter, pageIndex, pageSize } = args;
  const filters: DbpjkitWorkerJobRunsBoolExp[] = [{ dbpjkitWorkerJobId: { _eq: jobId } }];
  if (statusFilter !== "all") {
    filters.push({ statusId: { _eq: statusFilter } });
  }
  return {
    tenantId,
    where: { _and: filters },
    limit: pageSize,
    offset: pageIndex * pageSize,
  };
}

/** Reduce a raw job row to the summary fields shown on the detail card. */
export function normalizeJob(job: JobRaw): JobSummary {
  return {
    id: job.id,
    commandName: job.commandName,
    googleSpreadsheetId: job.googleSpreadsheetId,
    googleSheetId: job.googleSheetId,
    arguments: job.arguments,
    createdAt: job.createdAt,
    updatedAt: job.updatedAt,
  };
}

/** Map a run statusId to a Badge tone (V1's status colors, expressed in V2 tokens). */
export function statusTone(statusId?: string | null): NonNullable<BadgeProps["tone"]> {
  switch (statusId) {
    case "succeeded":
      return "score-a";
    case "failed":
      return "score-c";
    case "in_progress":
      return "score-b";
    case "init":
      return "primary";
    // canceled is a deliberate stop, not a failure — render it neutral rather than red.
    case "canceled":
      return "default";
    default:
      return "default";
  }
}

/**
 * Spreadsheet URL for the list cell — always appends `#gid=<sheetId>` (empty when
 * the sheet id is missing), matching V1's list-column link.
 */
export function listSpreadsheetUrl(spreadsheetId: string, sheetId?: number | null): string {
  const gid = sheetId != null ? String(sheetId) : "";
  return `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit#gid=${gid}`;
}

/**
 * Spreadsheet URL for the detail card — omits `#gid` when the sheet id is missing,
 * matching V1's `buildSpreadsheetUrl`.
 */
export function detailSpreadsheetUrl(spreadsheetId?: string | null, sheetId?: number | null): string | undefined {
  if (!spreadsheetId) return undefined;
  const base = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`;
  if (sheetId === undefined || sheetId === null) return base;
  return `${base}#gid=${sheetId}`;
}
